import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Phone, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DriverLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const requestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const checkRes = await axios.get(`${API}/auth/driver/check/${mobile}`);
      if (checkRes.data.status !== 'approved') {
        toast.error('Driver not approved yet. Please wait for admin approval.');
        setLoading(false);
        return;
      }
      const res = await axios.post(`${API}/auth/driver/request-otp`, { mobile });
      setGeneratedOtp(res.data.otp);
      toast.success('OTP generated!');
      setStep(2);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info('Mobile not registered. Redirecting to registration...');
        setTimeout(() => navigate('/mobileapp/register'), 1000);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send OTP');
      }
    } finally { setLoading(false); }
  };

  const verifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/driver/verify-otp`, { mobile, otp });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.driver));
      toast.success('Login successful!');
      navigate('/mobileapp/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)' }}>
      <Card className="w-full max-w-md shadow-xl border-slate-100">
        <CardHeader className="text-center space-y-3 pb-6">
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Driver Login</CardTitle>
          <CardDescription className="text-base">
            {step === 1 ? 'Enter your mobile number' : 'Enter the OTP'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <form onSubmit={requestOTP} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Mobile Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input data-testid="login-mobile-input" type="tel" placeholder="Enter 10-digit mobile" className="pl-10 h-12 bg-slate-50" value={mobile} onChange={(e) => setMobile(e.target.value)} pattern="[0-9]{10}" required />
                </div>
              </div>
              <Button type="submit" data-testid="request-otp-button" className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white text-base font-semibold rounded-full" disabled={loading}>
                {loading ? 'Sending...' : 'Send OTP'}
              </Button>
              <div className="text-center pt-2">
                <button type="button" onClick={() => navigate('/mobileapp/register')} className="text-sm text-slate-600 hover:text-slate-900">New driver? Register here</button>
              </div>
            </form>
          ) : (
            <form onSubmit={verifyOTP} className="space-y-5">
              {generatedOtp && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-emerald-700 mb-1">Your OTP</p>
                  <p className="text-3xl font-bold text-emerald-800 tracking-widest" data-testid="displayed-otp">{generatedOtp}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Enter OTP</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input data-testid="otp-input" placeholder="Enter 6-digit OTP" className="pl-10 h-12 bg-slate-50 text-center text-2xl tracking-widest" value={otp} onChange={(e) => setOtp(e.target.value)} maxLength="6" required />
                </div>
              </div>
              <Button type="submit" data-testid="verify-otp-button" className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white text-base font-semibold rounded-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>
              <div className="text-center pt-2">
                <button type="button" data-testid="back-button" onClick={() => { setStep(1); setOtp(''); setGeneratedOtp(''); }} className="text-sm text-slate-600 hover:text-slate-900">Change mobile number</button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverLogin;
